export const VIEW_OPTIONS = [
  { value: 'front', label: 'Front' },
  { value: 'angle', label: '45' },
  { value: 'back', label: 'Back' },
];

export const DEFAULT_ARTWORK_TRANSFORM = { offsetX: 0, offsetY: 0, scale: 1 };
export const EXPANDED_SCALE_MAX = 2.5;

const clampValue = (value, min, max) => Math.max(min, Math.min(max, value));

const DEFAULT_SCALE_BY_PRODUCT = {
  stickers: 1,
};

const DEFAULT_TRANSFORM_BY_PRODUCT_SIDE = {
  tshirt: {
    front: { offsetX: 0, offsetY: 0, scale: 1 },
    back: { offsetX: 0, offsetY: 0, scale: 1 },
  },
  hoody: {
    front: { offsetX: 0, offsetY: 0, scale: 1 },
    back: { offsetX: 0, offsetY: 0, scale: 1 },
  },
};

export const PRODUCT_ARTWORK_CONFIG = {
  tshirt: {
    sides: ['front', 'back'],
    labels: {
      front: 'Front art',
      back: 'Back art',
    },
    placement: {
      front: {
        offsetX: 0,
        offsetY: -0.06,
        size: [1, 1],
        bounds: { x: [-0.58, 0.58], y: [-0.82, 0.58] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 1,
      },
      back: {
        offsetX: 0,
        offsetY: -0.06,
        size: [1, 1],
        bounds: { x: [-0.58, 0.58], y: [-0.82, 0.58] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 1,
      },
      sleeveLeft: {
        offsetX: 0,
        offsetY: 0,
        size: [1, 1],
        bounds: { x: [-0.42, 0.42], y: [-0.42, 0.42] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 0.82,
      },
      sleeveRight: {
        offsetX: 0,
        offsetY: 0,
        size: [1, 1],
        bounds: { x: [-0.42, 0.42], y: [-0.42, 0.42] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 0.82,
      },
      neckLabelInner: {
        offsetX: 0,
        offsetY: 0,
        size: [1, 1],
        bounds: { x: [-0.34, 0.34], y: [-0.34, 0.34] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 0.72,
      },
    },
  },
  hoody: {
    sides: ['front', 'back'],
    labels: {
      front: 'Front art',
      back: 'Back art',
    },
    placement: {
      front: {
        offsetX: 0,
        offsetY: -0.1,
        size: [1, 1],
        bounds: { x: [-0.54, 0.54], y: [-0.76, 0.5] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 1,
      },
      back: {
        offsetX: 0,
        offsetY: -0.1,
        size: [1, 1],
        bounds: { x: [-0.54, 0.54], y: [-0.76, 0.5] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 1,
      },
      sleeveLeft: {
        offsetX: 0,
        offsetY: 0,
        size: [1, 1],
        bounds: { x: [-0.42, 0.42], y: [-0.42, 0.42] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 0.82,
      },
      sleeveRight: {
        offsetX: 0,
        offsetY: 0,
        size: [1, 1],
        bounds: { x: [-0.42, 0.42], y: [-0.42, 0.42] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 0.82,
      },
      neckLabelInner: {
        offsetX: 0,
        offsetY: 0,
        size: [1, 1],
        bounds: { x: [-0.34, 0.34], y: [-0.34, 0.34] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 0.72,
      },
    },
  },
  cap: {
    sides: ['art'],
    singleSided: true,
    labels: { art: 'Artwork' },
    placement: {
      art: {
        offsetX: -0.06,
        offsetY: 0.02,
        size: [1, 1],
        bounds: { x: [-0.78, -0.18], y: [0.02, 0.5] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 0.82,
        surfaceOffset: 0.014,
      },
    },
  },
  socks: {
    sides: ['art'],
    singleSided: true,
    labels: { art: 'Artwork' },
    placement: {
      art: {
        offsetX: -0.12,
        offsetY: -0.04,
        size: [0.34, 0.42],
        bounds: { x: [-0.44, 0.2], y: [-0.44, 0.64] },
        fitToBounds: true,
        scaleMin: 0.16,
        scaleMax: 0.9,
        surfaceOffset: 0.014,
      },
    },
  },
  stickers: {
    sides: ['design'],
    singleSided: true,
    shaped: true,
    labels: { design: 'Sticker art' },
    transformLocked: true,
    placement: {
      design: {
        offsetX: 0,
        offsetY: 0,
        size: [1.6, 1.0],
        bounds: { x: [-0.82, 0.82], y: [-0.72, 0.72] },
        scaleMin: 1,
        scaleMax: 1,
        surfaceOffset: 0.012,
      },
    },
  },
};

export const getArtworkConfig = (productType) => (
  PRODUCT_ARTWORK_CONFIG[productType] || PRODUCT_ARTWORK_CONFIG.tshirt
);

export const getArtworkSides = (productType) => getArtworkConfig(productType).sides;

export const getSidePlacementSpec = (productType, side) => (
  getArtworkConfig(productType).placement?.[side] || PRODUCT_ARTWORK_CONFIG.tshirt.placement.front
);

export const resolvePlacement = (productType, side, modelBounds, garmentScale = [1, 1, 1]) => {
  const spec = getSidePlacementSpec(productType, side);
  const { center, size, min, max } = modelBounds;
  const halfX = size.x / 2;
  const halfY = size.y / 2;
  const boundsCenterX = (spec.bounds.x[0] + spec.bounds.x[1]) / 2;
  const boundsCenterY = (spec.bounds.y[0] + spec.bounds.y[1]) / 2;
  const bounds = {
    minX: center.x + spec.bounds.x[0] * halfX,
    maxX: center.x + spec.bounds.x[1] * halfX,
    minY: center.y + spec.bounds.y[0] * halfY,
    maxY: center.y + spec.bounds.y[1] * halfY,
  };
  const boundsWidth = bounds.maxX - bounds.minX;
  const boundsHeight = bounds.maxY - bounds.minY;
  const planeSize = spec.fitToBounds
    ? [
      boundsWidth / Math.max(garmentScale[0] || 1, 0.001),
      boundsHeight / Math.max(garmentScale[1] || 1, 0.001),
    ]
    : spec.size;

  return {
    baseX: center.x + (boundsCenterX + spec.offsetX) * halfX,
    baseY: center.y + (boundsCenterY + spec.offsetY) * halfY,
    zFront: max.z + (spec.surfaceOffset ?? 0.024),
    zBack: min.z - (spec.surfaceOffset ?? 0.024),
    size: planeSize,
    bounds,
    scaleMin: spec.scaleMin,
    scaleMax: spec.scaleMax,
  };
};

export const getArtworkFootprint = (placement, scale, garmentScale, shaped, planeGeometry = null) => {
  let width = placement.size[0];
  let height = placement.size[1];

  if (planeGeometry) {
    planeGeometry.computeBoundingBox();
    const box = planeGeometry.boundingBox;
    width = box.max.x - box.min.x;
    height = box.max.y - box.min.y;
  }

  const scaleX = shaped ? scale : scale * garmentScale[0];
  const scaleY = shaped ? scale : scale * garmentScale[1];
  return { width: width * scaleX, height: height * scaleY };
};

export const clampArtworkTransform = (
  transform,
  placement,
  garmentScale,
  shaped,
  planeGeometry = null
) => {
  const scale = clampValue(transform.scale, placement.scaleMin, placement.scaleMax);
  const { width, height } = getArtworkFootprint(placement, scale, garmentScale, shaped, planeGeometry);

  const worldX = placement.baseX + transform.offsetX;
  const worldY = placement.baseY + transform.offsetY;
  const minX = placement.bounds.minX + width / 2;
  const maxX = placement.bounds.maxX - width / 2;
  const minY = placement.bounds.minY + height / 2;
  const maxY = placement.bounds.maxY - height / 2;

  const clampedX = minX <= maxX ? clampValue(worldX, minX, maxX) : placement.baseX;
  const clampedY = minY <= maxY ? clampValue(worldY, minY, maxY) : placement.baseY;

  return {
    offsetX: clampedX - placement.baseX,
    offsetY: clampedY - placement.baseY,
    scale,
  };
};

export const createArtworkState = (productType) => (
  Object.fromEntries(getArtworkSides(productType).map((side) => [side, null]))
);

export const getDefaultArtworkTransform = (productType, side) => ({
  ...DEFAULT_ARTWORK_TRANSFORM,
  scale: DEFAULT_SCALE_BY_PRODUCT[productType] ?? DEFAULT_ARTWORK_TRANSFORM.scale,
  ...(DEFAULT_TRANSFORM_BY_PRODUCT_SIDE[productType]?.[side] || {}),
});

export const clampTransformForSide = (productType, side, transform) => {
  const spec = getSidePlacementSpec(productType, side);
  const scaleMax = transform.mode === 'expanded' ? EXPANDED_SCALE_MAX : spec.scaleMax;
  return {
    ...transform,
    mode: transform.mode === 'expanded' ? 'expanded' : 'standard',
    scale: clampValue(transform.scale, spec.scaleMin, scaleMax),
  };
};

export const clampTransformsForProduct = (productType, transforms = {}) => (
  Object.fromEntries(
    getArtworkSides(productType).map((side) => [
      side,
      clampTransformForSide(productType, side, transforms[side] || getDefaultArtworkTransform(productType, side)),
    ])
  )
);

export const createArtworkTransforms = (productType) => clampTransformsForProduct(productType);

export const getViewOptions = (productType) => {
  if (['cap', 'socks', 'stickers'].includes(productType)) {
    return VIEW_OPTIONS.filter((option) => option.value !== 'back');
  }
  return VIEW_OPTIONS;
};

export const getPrintLocation = (artwork, productType) => {
  const sides = getArtworkSides(productType);
  if (sides.length === 1) return artwork[sides[0]] ? 'custom' : 'custom';

  const hasFront = Boolean(artwork.front);
  const hasBack = Boolean(artwork.back);
  if (hasFront && hasBack) return 'both';
  if (hasFront) return 'front';
  if (hasBack) return 'back';
  return 'custom';
};

export const buildArtworkBriefLines = (artwork, productType) => {
  const config = getArtworkConfig(productType);
  return config.sides.map((side) => {
    const label = config.labels[side] || side;
    return artwork[side] ? `${label}: ${artwork[side].name}` : `${label}: not uploaded`;
  }).join('\n');
};

// ponytail: dev-only sanity check — footprint at scaleMax must not exceed bounds on narrow models
if (import.meta.env?.DEV) {
  const narrowBounds = {
    center: { x: 0, y: 0, z: 0 },
    size: { x: 1.1, y: 2.55, z: 0.35 },
    min: { x: -0.55, y: -1.275, z: -0.175 },
    max: { x: 0.55, y: 1.275, z: 0.175 },
  };
  const garmentScale = [0.96, 0.96, 1];
  const placement = resolvePlacement('tshirt', 'front', narrowBounds);
  const { width, height } = getArtworkFootprint(placement, placement.scaleMax, garmentScale, false);
  const boundsW = placement.bounds.maxX - placement.bounds.minX;
  const boundsH = placement.bounds.maxY - placement.bounds.minY;
  console.assert(width <= boundsW + 1e-6 && height <= boundsH + 1e-6, 'tshirt max scale must fit print bounds');
  console.assert(
    clampTransformForSide('tshirt', 'front', { offsetX: 0, offsetY: 0, scale: 9 }).scale === placement.scaleMax,
    'product-switch clamp must cap scale to scaleMax'
  );
}
